<template lang="pug">
  v-tooltip(
    ref="vTooltip"
    :color="computedColor"
    :content-class="computedContentClass"
    v-bind="tooltipProps"
  )
    template(#activator="{ on, attrs }")
      span(v-bind="attrs" v-on="on")
        slot
    slot(name="tooltip") {{ text }}
</template>

<script>
export default {
  name: 'YzTooltip',
  props: {
    text: {
      type: String,
      default: ''
    },
    color: {
      type: String,
      default: 'black'
    },
    direction: {
      type: String,
      default: 'top',
      validator: value => ['top', 'bottom', 'left', 'right'].includes(value)
    },
    contentClass: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    openDelay: {
      type: [Number, String],
      default: 0
    },
    closeDelay: {
      type: [Number, String],
      default: 200
    },
    maxWidth: {
      type: [Number, String],
      default: 500
    },
    minWidth: {
      type: [Number, String],
      default: 0
    },
    nudgeBottom: {
      type: [Number, String],
      default: 0
    },
    nudgeLeft: {
      type: [Number, String],
      default: 0
    },
    nudgeRight: {
      type: [Number, String],
      default: 0
    },
    nudgeTop: {
      type: [Number, String],
      default: 0
    },
    nudgeWidth: {
      type: [Number, String],
      default: 0
    },
    offsetOverflow: {
      type: Boolean,
      default: false
    },
    openOnClick: {
      type: Boolean,
      default: false
    },
    openOnFocus: {
      type: Boolean,
      default: true
    },
    openOnHover: {
      type: Boolean,
      default: true
    },
    zIndex: {
      type: [Number, String],
      default: undefined
    },
    noArrow: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    computedColor() {
      // Handle different color formats
      if (this.color.startsWith('#')) {
        // Hex color - return as is
        return this.color
      } else if (this.color.startsWith('var(')) {
        // CSS variable - return as is
        return this.color
      } else if (this.color.startsWith('yz-')) {
        // Design system token with yz- prefix
        return `var(--${this.color})`
      } else {
        // Design system token without prefix - add yz-
        return `var(--yz-${this.color})`
      }
    },
    computedContentClass() {
      if (this.contentClass) {
        return this.contentClass
      }

      // If no arrow, return empty string
      if (this.noArrow) {
        return ''
      }

      // Create dynamic class with arrow color support
      const colorClass = this.getColorClass()
      return `tooltip-${this.direction}-arrow yz-tooltip-${this.direction}-arrow-${colorClass}`
    },
    tooltipProps() {
      const props = {
        [this.direction]: true,
        disabled: this.disabled,
        'open-delay': this.openDelay,
        'close-delay': this.closeDelay,
        'max-width': this.maxWidth,
        'min-width': this.minWidth,
        'nudge-bottom': this.nudgeBottom,
        'nudge-left': this.nudgeLeft,
        'nudge-right': this.nudgeRight,
        'nudge-top': this.nudgeTop,
        'nudge-width': this.nudgeWidth,
        'offset-overflow': this.offsetOverflow,
        'open-on-click': this.openOnClick,
        'open-on-focus': this.openOnFocus,
        'open-on-hover': this.openOnHover
      }
      
      if (this.zIndex !== undefined) {
        props['z-index'] = this.zIndex
      }

      return props
    }
  },
  mounted() {
    // Inject dynamic CSS for arrow colors
    this.injectArrowStyles()
    this.patchVTooltipScrollOffset()
  },
  updated() {
    // Update styles when color changes
    this.injectArrowStyles()
  },
  methods: {
    patchVTooltipScrollOffset() {
      const vt = this.$refs.vTooltip
      if (!vt || vt.__yzScrollPatched) return
      vt.__yzScrollPatched = true
      // Vuetify's menuable computes tooltip position using window.pageYOffset.
      // After SPA navigation in this app, the document scroll lives on
      // document.documentElement.scrollTop while window.pageYOffset reads 0,
      // so relativeYOffset is wrong by exactly that amount and the tooltip
      // is misplaced vertically. Compensate by adjusting the v-application
      // offsetParent rect that menuable measures inside its rAF callback.
      const originalUpdate = vt.updateDimensions.bind(vt)
      const originalRect = vt.getRoundedBoundedClientRect.bind(vt)
      vt.updateDimensions = function () {
        const docScroll = document.documentElement.scrollTop || document.body.scrollTop || 0
        const delta = docScroll - (window.pageYOffset || 0)
        if (!delta) {
          if (vt.getRoundedBoundedClientRect !== originalRect) {
            vt.getRoundedBoundedClientRect = originalRect
          }
          return originalUpdate()
        }
        vt.getRoundedBoundedClientRect = function (el) {
          const rect = originalRect(el)
          const cls = (el && el.className && el.className.toString()) || ''
          if (el && el.tagName === 'DIV' && cls.indexOf('v-application') !== -1) {
            return { ...rect, top: rect.top + delta }
          }
          return rect
        }
        return originalUpdate()
      }
    },
    getColorClass() {
      // Generate a unique class identifier for the color
      if (this.color.startsWith('#')) {
        // For hex colors, remove # and make it a valid class name
        return this.color.replace('#', 'hex-')
      } else if (this.color.startsWith('var(')) {
        // For CSS variables, extract variable name
        const match = this.color.match(/var\(--([^)]+)\)/)
        return match ? match[1].replace(/[^a-zA-Z0-9-]/g, '-') : 'custom'
      } else {
        // For design system tokens, use as-is (cleaned)
        return this.color.replace(/[^a-zA-Z0-9-]/g, '-')
      }
    },
    injectArrowStyles() {
      // Skip arrow injection if noArrow is true
      if (this.noArrow) {
        return
      }

      const colorClass = this.getColorClass()
      const styleId = `yz-tooltip-arrow-${colorClass}`

      // Remove existing style if it exists
      const existingStyle = document.getElementById(styleId)
      if (existingStyle) {
        existingStyle.remove()
      }

      // Create new style element
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `
        .yz-tooltip-top-arrow-${colorClass}::before {
          border-top: solid 8px ${this.computedColor} !important;
        }
        .yz-tooltip-bottom-arrow-${colorClass}::before {
          border-bottom: solid 8px ${this.computedColor} !important;
        }
        .yz-tooltip-right-arrow-${colorClass}::before {
          border-right: solid 8px ${this.computedColor} !important;
        }
        .yz-tooltip-left-arrow-${colorClass}::before {
          border-left: solid 8px ${this.computedColor} !important;
        }
      `

      // Append to head
      document.head.appendChild(style)
    }
  }
}
</script>

<style lang="scss" scoped>
// Component wrapper styles
.yz-tooltip {
  display: inline-block;
}
</style>